/** global handlerVars */

/**
 * Class for FileUpload
 */
class FileUpload {
  /**
   * Setup modules
   *
   * @return {void}
   */
  static setupModules() {
    const moduleEls = document.querySelectorAll(
      '.notary-file-upload-container',
    );

    for (const moduleEl of moduleEls) {
      const module = new FileUpload(moduleEl);
      module.init();
    }
  }

  /**
   * Constructor for FileUpload class
   *
   * @param {HTMLElement} moduleEl
   */
  constructor(moduleEl) {
    this.containerEl = moduleEl;
    this.fileUploadInputEl = this.containerEl.querySelector('.fme_upload_file');
    this.errorMessageEl = this.containerEl.querySelector('.file-upload-error');
  }

  /**
   * Initialize the module
   *
   * @return {void}
   */
  init() {
    this.addEvents();
  }

  /**
   * Add events
   *
   * @return {void}
   */
  addEvents() {
    this.fileUploadInputEl.addEventListener('change', (event) =>
      this.uploadFile(event),
    );
  }

  /**
   * Validate the file
   *
   * @param {File} file
   * @return {boolean}
   */
  validateFile(file) {
    if (!file) {
      this.displayError(handlerVars.errorMessages.FILE_NOT_FOUND);
      return false;
    }

    // Check if mime type is allowed
    if (!Object.values(handlerVars.mimeTypes).includes(file.type)) {
      this.displayError(handlerVars.errorMessages.FILE_TYPE);
      return false;
    }

    // Check if file size exceed the limit
    if (file.size > handlerVars.maxFileSize) {
      this.displayError(handlerVars.errorMessages.FILE_LIMIT);

      return false;
    }

    return true;
  }

  /**
   * Fetch the document upload URL
   *
   * @param {File} file
   * @param {String} documentId
   * @return {Promise<Object|null>}
   */
  async fetchDocumentUploadUrl(file, documentId) {
    if (!file.name) {
      this.logError('Invalid file name');
      throw new Error('Invalid file name');
    }

    const data = new URLSearchParams();
    data.append('documentId', documentId);
    data.append('name', file.name);
    data.append('security', file.name);

    const response = await fetch(handlerVars.endpoints.CREATE_DOCUMENT, {
      method: 'POST',
      body: data,
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
      },
    });

    const result = await response.json();

    if (!result.documentId) {
      this.displayError(handlerVars.errorMessages.UPLOAD_URL);
      this.logError(handlerVars.errorMessages.UPLOAD_URL);

      return null;
    }

    return result;
  }

  /**
   * Upload the file to the bucket
   *
   * @param {Object} uploadUrlData
   * @param {File} file
   * @return {Promise<void>}
   */
  async uploadToBucket(uploadUrlData, file) {
    // Proceed with the next request using uploadUrlData
    const form = new FormData();

    Object.entries(uploadUrlData.fields).forEach(([key, value]) => {
      form.append(key, value);
    });

    form.append('file', file);

    try {
      const response = await fetch(uploadUrlData.url, {
        method: 'POST',
        body: form,
      });

      if (!response.ok) {
        this.logError(`Upload failed with status ${response.status}`);
        throw new Error(`Upload failed with status ${response.status}`);
      }

      return true;
    } catch (error) {
      this.logError(`Upload failed with status ${response.status}`);
      console.error('Error during upload', error);
    }
  }

  /**
   * Update the document status
   *
   * @param {string} status
   * @return {void}
   */
  async updateDocumentStatus(
    status = handlerVars.uploadStatuses.UPLOADED,
    documentId,
  ) {
    const data = new URLSearchParams();
    data.append('status', status);

    try {
      const response = await fetch(
        handlerVars.endpoints.UPDATE_DOCUMENT_STATUS.replace(
          '{{documentId}}',
          documentId,
        ),
        {
          method: 'POST',
          body: data,
          headers: {
            'Content-Type': 'application/x-www-form-urlencoded',
          },
        },
      );

      if (!response.ok) {
        this.logError(`Document status failed with status ${response.status}`);

        throw new Error(
          `Document status failed with status ${response.status}`,
        );
      }

      return true;
    } catch (error) {
      this.logError(`Error during update ${error.toString()}`);
      console.error(`Error during update ${error.toString()}`);
    }
  }

  /**
   * Display an error message
   *
   * @param {string} message
   * @return {void}
   */
  displayError(message) {
    if (!this.errorMessageEl) return;

    this.errorMessageEl.style.display = 'block';
    this.errorMessageEl.innerHTML = message;
  }

  /**
   * Clear error messages
   *
   * @return {void}
   */
  clearMessages() {
    if (!this.errorMessageEl) return;

    this.errorMessageEl.innerHTML = '';
    this.errorMessageEl.style.display = 'none';
  }

  /**
   * Upload the file
   *
   * @param {Event} event
   * @return {Promise<void>}
   */
  async uploadFile(event) {
    const file = event.target.files[0];

    if (!this.validateFile(file)) {
      return;
    }

    const documentId = this.generateDocumentID();

    try {
      const uploadUrlData = await this.fetchDocumentUploadUrl(file, documentId);

      if (!uploadUrlData) {
        console.error('Failed to get upload URL');
        return;
      }

      const bucketUploadSuccess = await this.uploadToBucket(
        uploadUrlData,
        file,
      );

      if (!bucketUploadSuccess) {
        console.error('Failed to upload file');
        return;
      }

      // Set update document status
      const updateDocumentStatus = bucketUploadSuccess
        ? handlerVars.uploadStatuses.UPLOADED
        : handlerVars.uploadStatuses.FAILED;

      const statusUpdateSuccess = await this.updateDocumentStatus(
        updateDocumentStatus,
        uploadUrlData.documentId,
      );

      if (!statusUpdateSuccess) {
        console.error('Failed to update status');
        return;
      }

      // Add documentId to WC Session
      const updateWCSession = await this.updateWCSession(file.name, documentId);

      if (!updateWCSession) {
        console.error('Failed to update sessino');
        return;
      }

      // Clear any error messages
      this.clearMessages();
    } catch (error) {
      this.logError(`Error during upload ${error.toString()}`);
    }
  }

  /**
   * Generate document id
   *
   * @returns {string}
   */
  generateDocumentID() {
    return crypto.randomUUID();
  }

  /**
   * Update WC Session with documentId
   *
   * @param {String} fileName
   * @param {String} documentId
   * @returns {Promise<boolean>}
   */
  async updateWCSession(fileName, documentId) {
    const data = new FormData();
    data.append('action', 'update_wc_session');
    data.append('fileName', fileName);
    data.append('documentId', documentId);
    data.append('security', handlerVars.security);

    try {
      const response = await fetch(handlerVars.ajaxUrl, {
        method: 'POST',
        credentials: 'same-origin',
        body: data,
      });

      if (!response.ok) {
        console.log('not ok');
        throw new Error(
          `Error updating WC Session with status ${response.status}`,
        );
      }

      return true;
    } catch (error) {
      console.error('Error updating WC Session', error);
    }
  }

  /**
   * Log error message
   *
   * @param {string} message
   * @returns {Promise<boolean>}
   */
  async logError(message) {
    const data = new URLSearchParams();
    data.append('action', 'log_error');
    data.append('security', handlerVars.security);
    data.append('message', message);

    try {
      const response = await fetch(handlerVars.ajaxUrl, {
        method: 'POST',
        credentials: 'same-origin',
        body: data,
      });

      if (!response.ok) {
        throw new Error(`Error logging failed with status ${response.status}`);
      }

      return true;
    } catch (error) {
      console.error('Error during logging', error);
    }
  }
}

// Wait for DOM to be interactive
if (
  document.readyState === 'interactive' ||
  document.readyState === 'complete'
) {
  FileUpload.setupModules();
} else {
  document.addEventListener('DOMContentLoaded', FileUpload.setupModules);
}
